import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiLoader, FiCheckCircle, FiAlertCircle, FiMinus, FiClock } from 'react-icons/fi';

const CONFIGS = {
  loading:     { label: '…',            Icon: FiLoader,       cls: 'text-slate-400 bg-slate-50 border-slate-200',   spin: true  },
  not_started: { label: 'Not Started',  Icon: FiMinus,        cls: 'text-slate-400 bg-slate-50 border-slate-200',   spin: false },
  running:     { label: 'Running',      Icon: FiClock,        cls: 'text-amber-600 bg-amber-50 border-amber-200',   spin: false },
  processing:  { label: 'Processing',   Icon: FiLoader,       cls: 'text-amber-600 bg-amber-50 border-amber-200',   spin: true  },
  completed:   { label: 'Completed',    Icon: FiCheckCircle,  cls: 'text-green-700 bg-green-50 border-green-200',   spin: false },
  failed:      { label: 'Failed',       Icon: FiAlertCircle,  cls: 'text-red-600 bg-red-50 border-red-200',         spin: false },
  no_proposals:{ label: 'No Proposals', Icon: FiMinus,        cls: 'text-slate-500 bg-slate-100 border-slate-300',  spin: false },
};

// Statuses for which a "completed out of total" count is meaningful (i.e. a crosswalk has
// actually run for this assessment). Skipped for not_started / loading.
const COUNT_STATUSES = new Set(['running', 'processing', 'completed', 'failed', 'no_proposals']);

// Floating tooltip rendered into a portal so it escapes the data table's clipped (overflow)
// scroll container and always paints above every row — including the first one.
const BreakdownTooltip = ({ anchorRect, total, rows }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });

  useLayoutEffect(() => {
    if (!anchorRect || !ref.current) return;
    const el = ref.current.getBoundingClientRect();
    const gap = 8;
    let left = anchorRect.left + anchorRect.width / 2 - el.width / 2;
    // Keep the card within the viewport horizontally.
    left = Math.max(8, Math.min(left, window.innerWidth - el.width - 8));
    const top = anchorRect.top - el.height - gap; // above the badge
    setPos({ top, left, ready: true });
  }, [anchorRect]);

  // Center the caret on the anchor even after the card is clamped to the viewport.
  const caretLeft = anchorRect ? anchorRect.left + anchorRect.width / 2 - pos.left : 0;

  return createPortal(
    <div
      ref={ref}
      role="tooltip"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex: 9999,
        opacity: pos.ready ? 1 : 0,
      }}
      className="pointer-events-none transition-opacity duration-150"
    >
      <div className="w-44 rounded-lg bg-[#53545a] px-3 py-2.5 text-left shadow-xl ring-1 ring-black/5">
        <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-white/70">
          <span>Questionnaires</span>
          <span className="tabular-nums text-white/90">{total}</span>
        </div>
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.key} className="flex items-center gap-2 text-xs text-white/90">
              <span className={`h-2 w-2 flex-shrink-0 rounded-full ${r.dot}`} />
              <span className="flex-1">{r.label}</span>
              <span className="font-semibold tabular-nums text-white">{r.value}</span>
            </div>
          ))}
        </div>
      </div>
      {/* caret */}
      <span
        className="absolute top-full h-0 w-0 border-4 border-transparent border-t-[#53545a]"
        style={{ left: caretLeft, transform: 'translateX(-50%)' }}
      />
    </div>,
    document.body
  );
};

const CrosswalkStatusBadge = ({ status, completedCount, runningCount, failedCount, total }) => {
  const cfg = CONFIGS[status] || CONFIGS.not_started;
  const { label, Icon, cls, spin } = cfg;

  const badgeRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);

  // Show e.g. "Completed 2/3" so users can see progress across an assessment's questionnaires.
  const showCount =
    COUNT_STATUSES.has(status) &&
    typeof total === 'number' && total > 0 &&
    typeof completedCount === 'number';

  // Hover tooltip: per-questionnaire breakdown. Only meaningful once a crosswalk has run.
  const hasBreakdown = typeof total === 'number' && total > 0;
  const completed = typeof completedCount === 'number' ? completedCount : 0;
  const running   = typeof runningCount   === 'number' ? runningCount   : 0;
  const failed    = typeof failedCount    === 'number' ? failedCount    : 0;
  // Anything not accounted for by the three tracked states (e.g. no_proposals / not_started).
  const other = Math.max(0, total - completed - running - failed);

  const rows = [
    { key: 'completed', label: 'Completed', value: completed, dot: 'bg-green-500' },
    { key: 'running',   label: 'Running',   value: running,   dot: 'bg-amber-500' },
    { key: 'failed',    label: 'Failed',    value: failed,    dot: 'bg-red-500'   },
    { key: 'other',     label: 'Pending',   value: other,     dot: 'bg-slate-400' },
  ];

  const show = () => {
    if (badgeRef.current) setAnchorRect(badgeRef.current.getBoundingClientRect());
  };
  const hide = () => setAnchorRect(null);

  return (
    <span
      ref={badgeRef}
      onMouseEnter={hasBreakdown ? show : undefined}
      onMouseLeave={hasBreakdown ? hide : undefined}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${hasBreakdown ? 'cursor-default' : ''} ${cls}`}
    >
      <Icon className={`flex-shrink-0 ${spin ? 'animate-spin' : ''}`} style={{ fontSize: 11 }} />
      {label}
      {showCount && (
        <span className="font-semibold tabular-nums">{completedCount}/{total}</span>
      )}
      {hasBreakdown && anchorRect && (
        <BreakdownTooltip anchorRect={anchorRect} total={total} rows={rows} />
      )}
    </span>
  );
};

export default CrosswalkStatusBadge;
